/**
 * Locale detection and user-facing copy.
 *
 * The widget is bilingual (it/en) because the catalog is. Detection mirrors
 * the chat-api's STORE_VIEWS contract: {it, en} are the only configured
 * store views, so we map anything-other-than-it to en.
 */

export type Locale = 'it' | 'en';

/**
 * Read locale from `<html lang>`. Anything that doesn't start with "it"
 * (case-insensitive) falls back to en. The two-letter slice covers
 * variants like "it-IT", "en-US", "en-GB".
 */
export function detectLocale(htmlLang: string | null | undefined): Locale {
  return (htmlLang ?? 'en').toLowerCase().slice(0, 2) === 'it' ? 'it' : 'en';
}

/**
 * Resolve locale from a `?lang=` query param if present, else `<html lang>`.
 * Lets a single deployed widget URL serve both languages via
 * `?lang=it` / `?lang=en`, useful for shareable demo links.
 */
export function resolveLocale(
  urlSearch: string,
  htmlLang: string | null | undefined,
): Locale {
  const queryLang = new URLSearchParams(urlSearch).get('lang');
  return detectLocale(queryLang ?? htmlLang);
}

/**
 * Categorical error reasons surfaced to the user. We map raw HTTP status,
 * network failures, and abort signals onto these.
 */
export type ErrorKind =
  | 'rateLimit'
  | 'network'
  | 'timeout'
  | 'badRequest'
  | 'serverError'
  | 'generic';

const COPY: Record<Locale, Record<ErrorKind, string>> = {
  en: {
    rateLimit: "You're sending messages too quickly. Please wait a moment and try again.",
    network: "I can't reach the consultant right now. Check your connection and try again.",
    timeout: 'The consultant is taking too long to respond. Please try again.',
    badRequest: "I didn't quite understand that — could you rephrase?",
    serverError: 'The consultant is having trouble at the moment. Please try again in a bit.',
    generic: 'Something went wrong. Please try again.',
  },
  it: {
    rateLimit: 'Stai inviando messaggi troppo velocemente. Aspetta un momento e riprova.',
    network: 'Non riesco a raggiungere il consultant. Controlla la connessione e riprova.',
    timeout: 'Il consultant ci sta mettendo troppo a rispondere. Riprova.',
    badRequest: 'Non ho capito bene la richiesta — puoi riformularla?',
    serverError: 'Il consultant ha qualche problema al momento. Riprova tra poco.',
    generic: 'Qualcosa è andato storto. Riprova.',
  },
};

export function errorMessage(kind: ErrorKind, locale: Locale): string {
  return COPY[locale][kind];
}

/**
 * Static UI chrome strings that appear in `index.html` or are produced
 * imperatively in `main.ts`. Centralized here so a single locale switch
 * flips the whole surface (kicker, placeholder, send button, aria labels)
 * without scattering ternaries through the DOM glue.
 */
export interface UICopy {
  kicker: string;
  inputPlaceholder: string;
  inputAriaLabel: string;
  sendButton: string;
  thinkingAriaLabel: string;
}

const UI_COPY: Record<Locale, UICopy> = {
  en: {
    kicker: "A boutique perfumery advisor. Tell me what you're looking for.",
    inputPlaceholder: 'Try: a fresh citrus perfume for summer evenings under €150',
    inputAriaLabel: 'Ask the consultant',
    sendButton: 'Ask',
    thinkingAriaLabel: 'Consultant is thinking',
  },
  it: {
    kicker: 'Un consulente di profumeria boutique. Dimmi cosa cerchi.',
    inputPlaceholder: 'Prova: un profumo agrumato e fresco per le serate estive sotto i 150€',
    inputAriaLabel: 'Chiedi al consultant',
    sendButton: 'Chiedi',
    thinkingAriaLabel: 'Consultant sta pensando',
  },
};

export function uiCopy(locale: Locale): UICopy {
  return UI_COPY[locale];
}
